import { useMemo, useState } from "react";
import { ArrowUturnLeftIcon, CheckIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, EmptyState, Field, Input } from "../ui";
import { useDraft } from "../../hooks/useDraft";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { kindOf, useEducationLevels, useFieldsOfStudy, useScholarshipChange } from "./model";
import { canEditSetUp } from "./access";
import ReasonField, { reasonErrorOf, withReason } from "./ReasonField";
import ProgrammePicker from "./ProgrammePicker";

const NO_CHANGES = {};

// The rules as a form: lists for the "any of" kinds, one number for the grade
export const eligibilityFormOf = (rules = []) => ({
  levels: rules.filter((r) => r.kind === "CURRENT_LEVEL").map((r) => r.value),
  fields: rules.filter((r) => r.kind === "FIELD_OF_STUDY").map((r) => r.value),
  minGrade: rules.find((r) => r.kind === "MIN_GRADE_PERCENT")?.value ?? "",
});

export const eligibilityRulesOf = (form) => [
  ...form.levels.map((value) => ({ kind: "CURRENT_LEVEL", value })),
  ...form.fields.map((value) => ({ kind: "FIELD_OF_STUDY", value })),
  ...(String(form.minGrade).trim() ? [{ kind: "MIN_GRADE_PERCENT", value: String(form.minGrade).trim() }] : []),
];

const sameForm = (a, b) => JSON.stringify(eligibilityRulesOf(a)) === JSON.stringify(eligibilityRulesOf(b));

export const validateEligibility = ({ minGrade }) => {
  const errors = {};
  const grade = Number(minGrade);
  if (String(minGrade).trim() && !(grade >= 0 && grade <= 100)) errors.minGrade = "Enter a percentage from 0 to 100";
  return errors;
};

export function CheckboxList({ label, info, options, selected, onChange, disabled }) {
  const toggle = (value) => onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  return (
    <Field label={label} info={info}>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 has-[:checked]:border-brand has-[:checked]:bg-brand/5">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} disabled={disabled} />
            {o.label}
          </label>
        ))}
      </div>
    </Field>
  );
}

/**
 * Who may apply, per level of study of the offered programmes (part of journey step 2), saved with
 * `PUT …/levels/{levelId}/eligibility`: every programme of a level shares its rules. Rules of one
 * kind are alternatives ("any of these levels"); every kind that is set must hold. Students who miss
 * them may still apply: evaluators and admins see the application flagged. Changes are allowed
 * while the set-up is open (see `setUpOpen`).
 */
export default function EligibilityTab({ scholarship }) {
  const { one } = kindOf(scholarship.type);
  const change = useScholarshipChange(scholarship.id);
  const levelsOffered = useMemo(
    () => (scholarship.levels ?? []).map((l) => ({ id: l.level.id, name: l.level.name ?? "Unnamed level", eligibility: l.eligibility })),
    [scholarship.levels]
  );
  const [levelId, setLevelId] = useState(levelsOffered[0]?.id ?? "");
  const level = levelsOffered.find((l) => l.id === levelId) ?? levelsOffered[0];
  const programmesOfLevel = (scholarship.programmes ?? []).filter((p) => p.level?.id === level?.id).map((p) => p.name);
  const [draft, setDraft, clearDraft] = useDraft(`scholarship-eligibility:${scholarship.id}:level:${level?.id}`, NO_CHANGES);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const rules = useMemo(() => level?.eligibility ?? [], [level]);
  // Rules the level has keep their level or field even once it is retired
  const savedRules = (kind) => rules.filter((r) => r.kind === kind).map((r) => ({ id: r.value, name: r.label }));
  const { data: levels = [] } = useEducationLevels({ keep: savedRules("CURRENT_LEVEL") });
  const { data: fields = [] } = useFieldsOfStudy({ keep: savedRules("FIELD_OF_STUDY") });

  const editable = canEditSetUp(scholarship);
  const saved = useMemo(() => eligibilityFormOf(rules), [rules]);
  const form = draft.form ?? saved;
  const reason = draft.reason ?? "";
  const changed = !sameForm(form, saved);

  const update = (patch) => {
    setErrors({});
    setDraft((prev) => {
      const next = { ...form, ...patch };
      return sameForm(next, saved) ? (prev.reason ? { reason: prev.reason } : NO_CHANGES) : { ...prev, form: next };
    });
  };

  const discard = () => {
    clearDraft();
    setErrors({});
  };

  const copyFrom = (source) => {
    update(eligibilityFormOf(source.eligibility));
    toast.success(`Copied the rules of ${source.name}`);
  };

  const save = async () => {
    const found = validateEligibility(form);
    const reasonError = reasonErrorOf(scholarship, reason);
    if (reasonError) found.reason = reasonError;
    if (Object.keys(found).length) return setErrors(found);
    const body = withReason(scholarship, { rules: eligibilityRulesOf(form) }, reason);
    setSaving(true);
    try {
      await change((etag) => scholarshipsApi.put(`admin/scholarships/${scholarship.id}/levels/${level.id}/eligibility`, body, { ifMatch: etag }));
      toast.success(`Eligibility for ${level.name} saved`);
      discard();
    } catch (error) {
      console.error(`Failed to save eligibility of level ${level.id} on scholarship ${scholarship.id}:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save eligibility. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editable || saving;

  if (!levelsOffered.length) {
    return <EmptyState title="No programmes yet" description={`Add the programmes this ${one} offers first; eligibility is set for each of their levels of study.`} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <ProgrammePicker
        label="Level of study"
        programmes={levelsOffered}
        programme={level}
        onChange={(id) => {
          setLevelId(id);
          setErrors({});
        }}
        countOf={(p) => (p.eligibility ?? []).length}
        item="rule"
        copyLabel="Copy rules from"
        onCopy={editable ? copyFrom : null}
        disabled={saving}
      />
      <p className="text-sm text-slate-600">Applies to: {programmesOfLevel.join(", ")}</p>
      {changed && <p className="text-sm text-amber-700">Unsaved changes</p>}

      <CheckboxList
        label="Current level of study"
        options={levels.map((l) => ({ value: l.id, label: l.levelName }))}
        selected={form.levels}
        onChange={(levelIds) => update({ levels: levelIds })}
        disabled={disabled}
      />
      <CheckboxList
        label="Field of study"
        options={fields.map((f) => ({ value: f.id, label: f.fieldName }))}
        selected={form.fields}
        onChange={(fieldIds) => update({ fields: fieldIds })}
        disabled={disabled}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          type="number"
          label="Minimum final grade"
          suffix="%"
          min={0}
          max={100}
          step="0.1"
          placeholder="No minimum"
          value={form.minGrade}
          onChange={(e) => update({ minGrade: e.target.value })}
          error={errors.minGrade}
          disabled={disabled}
        />
      </div>

      {editable && (
        <div className="flex flex-col gap-4 border-t border-slate-100 pt-5">
          <ReasonField
            scholarship={scholarship}
            value={reason}
            onChange={(value) => {
              setErrors({});
              setDraft((prev) => ({ ...prev, reason: value }));
            }}
            error={errors.reason}
            disabled={saving}
          />
          <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
            <Button variant="ghost" onClick={discard} disabled={saving || (!changed && !reason)} icon={<ArrowUturnLeftIcon className="h-4 w-4" />}>
              Discard changes
            </Button>
            <Button onClick={save} loading={saving} disabled={!changed} icon={<CheckIcon className="h-4 w-4" />}>
              Save eligibility
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
